import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { setDefaultCountryField } from '../../../../helpers/countriesHelpers';
import { t } from '../../../../i18n';
import { translationsRootKey } from '../../../../settings';

const noDivisionsId = 'noDivisions';

const initState = {
  company: null,
  editForm: null,
  isEdit: false,
  loading: false,
  formInfo: {
    countries: [],
  },
  filteredDivisions: [],
  filteredUnits: [],
  deleted: false,
  baseError: null,
};

const initialCompanyData = {
  account_manager: null,
  account_size: 'unknown',
  activation_rep: null,
  active: 'false',
  address: '',
  approval: 'manual',
  backups_enabled: true,
  breaks_compensation: false,
  breaks_duration: 0,
  breaks_enabled: false,
  city: '',
  company_image_url: '',
  company_logo_url: '',
  cost_center: null,
  country: '',
  credit_days: 7,
  direct_units: [],
  divisions: [],
  invoice_enabled: false,
  invoices_by_location: false,
  legal_entity_id: '',
  name: '',
  rollup: 'job',
  sales_rep: null,
  state: '',
  street: '',
  zip: '',
  booked_tier_pay_rate_enabled: null,
};

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const { ...restProps } = data;

  const company = {
    ...restProps,
    address: '',
  };

  return {
    ...state,
    company,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function validate(attr, value) {
  let error = '';

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.companies.company.name_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.companies.company.country_title') });
    }
  }

  return error;
}

function onEdit(state, { payload }) {
  const { company } = payload;
  const propsArray = Object.keys(company);
  const divisionsProp = prop =>
    company[prop].length
      ? company[prop].map(item => {
          return { value: item.id, label: item.name };
        })
      : [{ value: noDivisionsId, label: t(`${translationsRootKey}.companies.company.no_divisions_label`) }];
  const directUnitsProp = prop =>
    prop === 'direct_units'
      ? company[prop].map(item => {
          return { value: item.id, label: item.name };
        })
      : company[prop];
  const resultProp = prop => (prop === 'divisions' ? divisionsProp(prop) : directUnitsProp(prop));

  const getFormValue = prop => {
    if (prop === 'state') {
      return company[prop]?.id;
    }

    return company[prop] && Object.prototype.hasOwnProperty.call(company[prop], 'id')
      ? company[prop].id
      : resultProp(prop);
  };
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: getFormValue(prop),
      error: prop === 'breaks_duration' && !company.breaks_enabled ? '' : validate(prop, company[prop]),
      touched: false,
    }),
  );

  const isNoDivisionsSelected = !company.divisions.length;

  editForm.divisions = {
    ...editForm.divisions,
    isNoDivisionsSelected,
  };

  editForm.active.value = editForm.active.value ? 'true' : 'false';

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    company: null,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  const baseError = data.base ? data.base[0] : null;

  return {
    ...state,
    editForm,
    baseError,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialCompanyData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialCompanyData[prop],
      error: validate(prop, initialCompanyData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    company: initialCompanyData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
      active: {
        ...editForm.active,
        value: state.formInfo.activation_statuses[0].id,
      },
      booked_tier_pay_rate_enabled: {
        ...editForm.booked_tier_pay_rate_enabled,
        value: state?.formInfo?.default_booked_tier_pay_rate_enabled || false,
      },
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
    baseError: null,
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      ...data,
    },
    filteredDivisions: [{ id: noDivisionsId, name: t(`${translationsRootKey}.companies.company.no_divisions_label`) }],
  };
}

function onLoadManagersFormData(state, { payload }) {
  const { data } = payload;

  const administrators = data.map(admin => {
    return {
      id: admin.id,
      name: `${admin.first_name} ${admin.last_name}`,
    };
  });

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      administrators,
    },
  };
}

function onFilterDivisionsSuccess(state, { payload }) {
  const { data } = payload;
  const filteredDivisions = [
    { id: noDivisionsId, name: t(`${translationsRootKey}.companies.company.no_divisions_label`) },
    ...data,
  ];

  return {
    ...state,
    filteredDivisions,
  };
}

function onChangeSelectedDivisions(state, { payload }) {
  const { divisions } = payload;
  const editForm = { ...state.editForm };

  const isNoDivisionsSelected = divisions.some(item => item.value === noDivisionsId);

  editForm.divisions = {
    value: isNoDivisionsSelected
      ? [{ value: noDivisionsId, label: t(`${translationsRootKey}.companies.company.no_divisions_label`) }]
      : divisions,
    error: '',
    name: 'divisions',
    touched: true,
    isNoDivisionsSelected,
  };

  editForm.direct_units = {
    value: isNoDivisionsSelected
      ? state.company.direct_units.map(item => {
          return { value: item.id, label: item.name };
        })
      : [],
    error: '',
    name: 'direct_units',
    touched: true,
  };

  return {
    ...state,
    editForm,
    filteredDivisions: isNoDivisionsSelected
      ? [{ id: noDivisionsId, name: t(`${translationsRootKey}.companies.company.no_divisions_label`) }]
      : state.filteredDivisions,
    baseError: null,
  };
}

function onFilterUnitsSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    filteredUnits: data,
  };
}

function onChangeSelectedUnits(state, { payload }) {
  const { direct_units } = payload;
  const editForm = { ...state.editForm };

  editForm.direct_units = {
    value: direct_units,
    error: '',
    name: 'direct_units',
    touched: true,
  };

  return {
    ...state,
    editForm,
    baseError: null,
  };
}

export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.CREATE]: onCreate,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_MANAGERS_FORM_DATA]: onLoadManagersFormData,
  [actions.FILTER_DIVISIONS_SUCCESS]: onFilterDivisionsSuccess,
  [actions.CHANGE_SELECTED_DIVISIONS]: onChangeSelectedDivisions,
  [actions.FILTER_UNITS_SUCCESS]: onFilterUnitsSuccess,
  [actions.CHANGE_SELECTED_UNITS]: onChangeSelectedUnits,
});
